import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import LogoCircle from 'ui/src/assets/images/ProtexxaLogos/ProtexxaLogo-circle.svg?react'
import LogoSymboleCircle from 'ui/src/assets/images/ProtexxaLogos/ProtexxaSymbole-circle.svg?react'
import ConciergeIcon from 'ui/src/assets/images/SignupFlow/concierge.svg?react'

import Box from 'ui/src/components/Box'
import { Tooltip } from 'ui/src/components/Tooltip'
import Typography from 'ui/src/components/Typography'
import Services from 'services'
import { SWR_COMPANY } from 'services/swr.keys'
import { useTheme, useMediaQuery } from '@mui/material'
import globalStore from 'store'
import { toastStyle } from 'util/toast'
import { useTranslation } from 'react-i18next'
import generateErrorMessage from 'util/errorMessage'
import { ProfileMenu, ReferralIcon, StyledContainer, StyledAvatar } from './styles'
import LanguageSelector from 'ui/src/components/LanguageSelector'
import { NotificationsIcon } from 'ui/src/assets/icons'
import IconButton from 'ui/src/components/IconButton'
import NotificationsMenu from 'pages/common/Notifications/components/Menu'

const AppHeader = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const logout = globalStore(state => state.logout)
  const isHighLevelUser = globalStore(state => state.isHighLevelUser)
  const user = globalStore(state => state.user)
  const avatarChanged = globalStore(state => state.avatarChanged)
  const getAvatar = globalStore(state => state.getAvatar)
  const avatar = globalStore(state => state.avatar)
  const setAvatar = globalStore(state => state.setAvatar)
  const register = globalStore(state => state.register)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [allNotifications, setAllNotifications] = useState<any>([])
  const [refetchData, setRefetchData] = useState(false)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const notificationCategoryMapping = {
    CRITICAL: 'critical',
    IMPORTANT: 'important',
    REPORT: 'image',
    REMINDER: 'reminders',
    COMPANY: 'company',
  }

  const bellIconColor = isNotificationsOpen ? '#4C89D3' : '#0056C0'
  const hasHighLevelPrivileges = isHighLevelUser()

  const isCompanyPage = useMemo(
    () => location.pathname.includes('/company') && !location.pathname.includes('individual'),
    [location.pathname],
  )

  const { t } = useTranslation()

  const handleLanguageChange = useCallback(
    async (language: string) => {
      const langCode = language.split('-')[0]
      if (user) await Services.User.updateLanguage(langCode)
    },
    [user],
  )

  // companyData
  const { data: companyData } = useSWR(
    hasHighLevelPrivileges ? SWR_COMPANY : null,
    () => user && Services.Company.getCompanyV2(),
  )

  useEffect(() => {
    const fetchNotifications = async () => {
      const result = await Services.Notifications.getAllNotifications()
      const userNotifications = result.filter(
        notification => notification.notificationCategory !== 'COMPANY',
      )
      const notifications = hasHighLevelPrivileges ? result : userNotifications
      const filteredNotifications = notifications.filter(notification => {
        const category = notificationCategoryMapping[notification.notificationCategory]
        return register?.notificationSettings?.push[category]
      })
      setAllNotifications(filteredNotifications)
    }
    fetchNotifications()
  }, [refetchData, hasHighLevelPrivileges, register])

  useEffect(() => {
    if (allNotifications?.some(notification => !notification.isRead)) {
      setHasUnreadNotifications(true)
    } else {
      setHasUnreadNotifications(false)
    }
  }, [allNotifications])

  const handleNotificationsClick = () => {
    setIsNotificationsOpen(!isNotificationsOpen)
  }

  const handleModalClose = () => {
    setIsNotificationsOpen(false)
  }

  const handleSignOut = useCallback(async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      if (error instanceof Error) toast.error(generateErrorMessage(error.message), toastStyle)
    }
  }, [logout])

  const renderProfileMenu = useMemo(() => {
    return (
      <ProfileMenu
        ref={ref}
        isopened={isProfileMenuOpen ? 1 : undefined}
        sx={{ backgroundColor: 'gray.0' }}
      >
        <Typography
          onClick={() => {
            if (hasHighLevelPrivileges) navigate('/company/settings')
            else {
              navigate('/profile')
            }
          }}
          variant="button"
          sx={{
            fontSize: 'subtitle1.fontSize',
            fontWeight: 'subtitle1.fontWeight',
            textTransform: 'none',
            cursor: 'pointer',
            padding: 1.5,
          }}
        >
          {t('app.myAccount')}
        </Typography>
        <Typography
          onClick={handleSignOut}
          variant="button"
          sx={{
            textTransform: 'none',
            cursor: 'pointer',
            color: 'feedback.negative.500',
            padding: 1.5,
          }}
        >
          {t('app.signOut')}
        </Typography>
      </ProfileMenu>
    )
  }, [handleSignOut, isProfileMenuOpen, navigate, hasHighLevelPrivileges, t])

  const headerOptions = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Tooltip title="Marketplace">
          <div>
            <ReferralIcon
              data-testid="marketplace"
              onClick={() => {
                navigate('marketplace')
              }}
            />
          </div>
        </Tooltip>

        <Box sx={{ mr: { xl: 4.5, lg: 4.5, md: 4.5, sm: 4.5, xs: 1.5 }, position: 'relative' }}>
          <IconButton
            sx={{ width: '100%', height: '100%' }}
            className="bell"
            onClick={handleNotificationsClick}
          >
            <NotificationsIcon sx={{ color: bellIconColor, size: 3, cursor: 'pointer' }} />
          </IconButton>
          <NotificationsMenu
            isOpen={isNotificationsOpen}
            allNotifications={allNotifications}
            setRefetchData={setRefetchData}
            setIsOpen={setIsNotificationsOpen}
          />
          {hasUnreadNotifications && (
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: '#BE0027',
                border: '1px solid #FFFFFF',
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            position: 'relative',
          }}
        >
          <StyledAvatar
            data-testid="avatar"
            sx={{ cursor: 'pointer' }}
            src={avatar}
            onClick={() => {
              setIsProfileMenuOpen(value => !value)
            }}
          />
          {renderProfileMenu}
        </Box>
        <Box
          onClick={() => {
            navigate('/concierge')
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            ml: { xl: 4, lg: 4, md: 4, sm: 4, xs: 2 },
          }}
        >
          <ConciergeIcon
            data-testid="concierge"
            style={{
              cursor: 'pointer',
              marginLeft: isSmallScreen ? '0.3125rem' : 0, //5px
              height: '1.09375rem', //17.5px
              width: 'auto',
            }}
          />
          <Typography
            variant="body3"
            sx={{
              color: 'primary.600',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          >
            Concierge
          </Typography>
        </Box>
        <Box
          sx={{
            ml: { xl: 2, lg: 2, md: 2, sm: 2, xs: 0 },
          }}
        >
          <LanguageSelector onChange={handleLanguageChange} />
        </Box>
      </Box>
    ),
    [
      avatar,
      navigate,
      renderProfileMenu,
      isNotificationsOpen,
      refetchData,
      hasUnreadNotifications,
      bellIconColor,
      allNotifications,
      handleNotificationsClick,
      isSmallScreen,
      handleLanguageChange,
    ],
  )

  useEffect(() => {
    const handleClickOutsideProfileTab = event => {
      if (ref.current && !ref.current.contains(event.target)) setIsProfileMenuOpen(false)
    }
    document.addEventListener('click', handleClickOutsideProfileTab, true)
    return () => {
      document.removeEventListener('click', handleClickOutsideProfileTab, true)
    }
  })

  useEffect(() => {
    if (isCompanyPage && companyData) setAvatar(companyData.image)
    if (!isCompanyPage && register) getAvatar()
  }, [companyData, register, hasHighLevelPrivileges, isCompanyPage, avatarChanged])

  return (
    <Box>
      <StyledContainer maxWidth="lg">
        {isNotificationsOpen && (
          <div
            onClick={handleModalClose}
            style={{
              width: '100vw',
              height: '100vh',
              backgroundColor: 'transparent',
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          />
        )}
        <Box
          sx={{
            display: { xl: 'flex', lg: 'flex', md: 'flex', sm: 'flex', xs: 'none' },
          }}
        >
          <LogoCircle
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (hasHighLevelPrivileges) {
                navigate('/company/dashboard')
              } else navigate('/dashboard')
            }}
          />
        </Box>
        <Box
          sx={{
            display: { xl: 'none', lg: 'none', md: 'none', sm: 'none', xs: 'flex' },
            width: 32,
          }}
        >
          <LogoSymboleCircle
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (hasHighLevelPrivileges) navigate('/company/dashboard')
              else navigate('/dashboard')
            }}
          />
        </Box>
        {headerOptions}
      </StyledContainer>
    </Box>
  )
}

export default AppHeader
